import { useState } from "react";
import { InputOwnProps } from ".";
import RequiredMark from "components/atoms/RequiredMark";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from "@mui/material";

export type InputProps = InputOwnProps & React.ComponentProps<typeof TextField>;

const CssTextField = styled(TextField)<{ icon?: boolean; customize?: boolean }>(
  ({ customize, icon }) => ({
    // Filled Input
    "& .MuiFilledInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      paddingLeft: icon ? "32px" : "0",
      paddingRight: 0,
      /* yelo-usernext's field border, the same line the design fields
         (DesignFieldRedux) draw, so MUI and design fields match. */
      border: "1px solid #ddd6df",
    },
    /* yelo-usernext's error state: a heavy border and a pale wash. An empty
       required field prints no message any more (utils/fieldError.util), so
       this carries the whole signal; `!important` on the fill so it holds
       through hover and focus until the value is valid. */
    "&.Mui-error .MuiFilledInput-root": {
      border: "2px solid #b61a5c",
      backgroundColor: "#fdecec !important",
    },
    "& .MuiFilledInput-input::placeholder": {
      color: "#B8B2BC !important",
      fontWeight: "400 !important",
      opacity: 1,
      WebkitTextFillColor: "#B8B2BC !important",
    },
    "& .MuiFilledInput-root:after, .MuiFilledInput-root:before": {
      display: "none",
    },
    "& .MuiFilledInput-root .Mui-disabled": {
      backgroundColor: "#ececec !important",
      opacity: "0.5",
      borderRadius: "12px",
    },
    "& .Mui-focused": {
      backgroundColor: "#ffffff !important",
      borderColor: "#520A76 !important",
    },
    "& .MuiFilledInput-input:hover": {
      borderRadius: "12px",
      backgroundColor: "#ffffff !important",
    },
    "& .Mui-disabled:hover": {
      backgroundColor: "#ececec !important",
    },
    "& .MuiInputBase-root.MuiFilledInput-root:hover": {
      backgroundColor: "#ffffff !important",
    },
    "& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled:hover": {
      backgroundColor: "#ececec !important",
    },
    "& .MuiFormLabel-root": {
      left: icon && "32px",
      maxWidth: icon && "100%",
    },
    "& .MuiFormHelperText-root": {
      color: "rgb(183, 33, 54)",
    },
    "& .MuiFilledInput-input": {
      direction: "inherit",
      padding: customize ? "16px" : "25px 12px 8px 12px",
    },
    "& .MuiInputBase-inputMultiline": {
      paddingTop: "0 !important",
    },
    /* No `boxShadow: "none"` here. That inset shadow is the only thing the
       engine accepts as an override for the background it paints on an
       autofilled field, so clearing it handed the field back to whatever
       colour the browser had chosen. App.scss owns this now. */
    "& input:-webkit-autofill": {
      borderRadius: "12px",
    },
    "& .MuiInputAdornment-root": {
      display: "none",
    },
  })
);

export default function Input({
  type,
  showIcon,
  StartIcon,
  StartIconProps,
  error,
  helperText,
  customize,
  markRequired,
  ...rest
}: InputProps & { markRequired?: boolean }) {
  const [show, setShow] = useState(false);

  /* As in yelo-usernext: the asterisk joins the label, and the control gets
     aria-required. Not MUI's own `required`, which also sets the native
     attribute and lets the browser's validation bubble fire over the app's
     own error state. */
  const label =
    markRequired && rest.label ? (
      <>
        {rest.label}
        <RequiredMark />
      </>
    ) : (
      rest.label
    );
  const inputProps = markRequired
    ? { ...rest.inputProps, "aria-required": true }
    : rest.inputProps;

  return (
    <>
      {StartIcon ? (
        <>
          <Box
            sx={{
              display: "flex",
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                left: "16px",
                top: "16px",
                position: "absolute",
                zIndex: 1,
              }}
            >
              <StartIcon
                style={{
                  fontSize: "18px",
                  color: "#939399",
                  ...StartIconProps,
                }}
              />
            </Box>
            <CssTextField
              fullWidth
              variant="filled"
              icon={true}
              error={error}
              {...rest}
              label={label}
              inputProps={inputProps}
            />
          </Box>
          {error && helperText && (
            <div
              style={{
                color: "rgb(183, 33, 54)",
                fontSize: "0.75rem",
                lineHeight: "1.66",
                marginTop: "3px",
                marginLeft: "14px",
                fontWeight: "400",
              }}
            >
              {helperText}
            </div>
          )}
        </>
      ) : (
        <>
          {customize && (
            <Typography
              mb={1}
              color={"#0a0a0e"}
              fontSize={"12px"}
              fontWeight={400}
              textTransform={"uppercase"}
            >
              {label}
            </Typography>
          )}
          <CssTextField
            customize={customize}
            fullWidth
            color="info"
            variant="filled"
            className="customize"
            helperText={helperText}
            error={error}
            type={show && showIcon && type === "password" ? "text" : type}
            {...rest}
            label={!customize ? label : ""}
            inputProps={inputProps}
            InputProps={{
              endAdornment: showIcon && type === "password" && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShow(!show)}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="toggle password visibility"
                  >
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              readOnly: rest?.readOnly,
              ...rest.InputProps,
            }}
          />
        </>
      )}
    </>
  );
}
